import * as XLSX from 'xlsx-js-style';
import type { SubprocessItemRow } from '../components/OSSubprocessItemsTable';
import { allowedYieldOf } from '../components/OSSubprocessItemsTable';
import type { SubprocessOption } from '../select/SubprocessCells';

interface ExportArgs {
  rows: SubprocessItemRow[];
  subprocesses: SubprocessOption[];
  mainIdent?: string;
  mainName?: string;
}

const headerStyle = {
  fill: { fgColor: { rgb: 'D9D9D9' } },
  font: { bold: true, color: { rgb: '000000' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: '999999' } },
    bottom: { style: 'thin', color: { rgb: '999999' } },
    left: { style: 'thin', color: { rgb: '999999' } },
    right: { style: 'thin', color: { rgb: '999999' } },
  },
};

const cellStyle = {
  alignment: { vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: 'DDDDDD' } },
    bottom: { style: 'thin', color: { rgb: 'DDDDDD' } },
    left: { style: 'thin', color: { rgb: 'DDDDDD' } },
    right: { style: 'thin', color: { rgb: 'DDDDDD' } },
  },
};

const safeFilenamePart = (value?: string) =>
  String(value || 'normativ')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80);

export function downloadOSInterProductsExcel({
  rows,
  subprocesses,
  mainIdent = '',
  mainName = '',
}: ExportArgs) {
  const subprocessById = new Map(subprocesses.map((sp) => [sp._id, sp]));
  const visibleRows = rows.filter((row) =>
    row.subprocessId ||
    row.issueSubprocessId ||
    row.itemId ||
    row.ident ||
    row.name ||
    row.quantity ||
    row.secondaryQty ||
    row.allowedWaste ||
    row.allowedYield
  );

  const data = visibleRows.map((row) => {
    const subprocess = row.subprocessId ? subprocessById.get(row.subprocessId) : undefined;
    const processDefinition =
      subprocess && typeof subprocess.processDefinition === 'object'
        ? subprocess.processDefinition
        : null;
    const issueSubprocess = row.issueSubprocessId ? subprocessById.get(row.issueSubprocessId) : undefined;
    const issueProcessDefinition =
      issueSubprocess && typeof issueSubprocess.processDefinition === 'object'
        ? issueSubprocess.processDefinition
        : null;

    return {
      'Главна шифра': mainIdent,
      'Главен назив': mainName,
      'Прием': subprocess?.name || '',
      'Издавање': issueSubprocess?.name || '',
      'Шифра': row.ident || '',
      'Назив': row.name || '',
      'Количина': row.quantity || '',
      'ЕМ': row.unitCode || '',
      'Количина споредна ЕМ': row.secondaryQty || '',
      'Споредна ЕМ': row.secondaryUnitCode || '',
      'Дозволен принос (ЕМ)': allowedYieldOf(row),
      'Дозволен отпад (%)': row.allowedWaste || '',
    };
  });

  const headers = [
    'Главна шифра',
    'Главен назив',
    'Прием',
    'Издавање',
    'Шифра',
    'Назив',
    'Количина',
    'ЕМ',
    'Количина споредна ЕМ',
    'Споредна ЕМ',
    'Дозволен принос (ЕМ)',
    'Дозволен отпад (%)',
  ];

  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
  worksheet['!cols'] = [
    { wch: 16 },
    { wch: 32 },
    { wch: 18 },
    { wch: 24 },
    { wch: 18 },
    { wch: 24 },
    { wch: 16 },
    { wch: 32 },
    { wch: 12 },
    { wch: 10 },
    { wch: 20 },
    { wch: 12 },
    { wch: 18 },
    { wch: 16 },
  ];
  worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(XLSX.utils.decode_range(worksheet['!ref'] || 'A1:N1')) };

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:N1');
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
    if (worksheet[cellRef]) worksheet[cellRef].s = headerStyle;
  }
  for (let r = 1; r <= range.e.r; r += 1) {
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (worksheet[cellRef]) worksheet[cellRef].s = cellStyle;
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Меѓу-Полупроизвод');
  XLSX.writeFile(
    workbook,
    `megu_poluproizvod_${safeFilenamePart(mainIdent)}_${new Date().toISOString().split('T')[0]}.xlsx`
  );
}
